import type { ArenaDef } from '@archon3d/core';
import { TargetCamera } from '@babylonjs/core/Cameras/targetCamera';
import { Matrix, Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { Scene } from '@babylonjs/core/scene';
import { fitArena, type Fit } from './arenaFit';
import { WHOLE_CANVAS, type ArenaFrame } from './arenaFrame';

/**
 * About 40 degrees above the horizontal: high enough to read the floor a luminosity cycle or a
 * cycling barrier is drawn on, shallow enough that a fighter still stands up rather than reading
 * as a disc.
 */
export const CAMERA_ELEVATION = (40 * Math.PI) / 180;
/** A long lens, so the tiles keep some parallax instead of reading flat. */
export const CAMERA_FOV = (28 * Math.PI) / 180;
/**
 * The only movement the camera is allowed: it eases this fraction closer as the fighters close on
 * each other. A camera the player did not move with their head is exactly what the pivot removed,
 * so nothing else here tracks, swings or shakes.
 */
export const PUSH_IN = 0.15;
/** How quickly the framing eases towards where it wants to be, per second. */
const FOLLOW = 2.5;
/**
 * A 1.8 m fighter across a 48-tile frame is about 20 px on a 1920 px window and under 10 per Quest
 * eye. Past that a `fit` arena has stopped being readable and wants `scroll` instead (doc 12).
 */
export const FIT_TILE_CEILING = 48;
/**
 * Room between the walls and the edge of the frame: the fit stands this much further off than
 * the walls alone need, so a fighter against a wall is not drawn against the screen's edge.
 */
export const FIT_MARGIN = 1.06;
/** What stands on the floor is taken to be at least this tall: a fighter at the far wall. */
const LEAST_TOP = 1.8;
/** The frame is never squeezed below this share of the canvas, either way, by what covers it. */
const LEAST_FRAME = 0.34;

const clamp01 = (value: number) => Math.max(0, Math.min(1, value));

export interface ArenaCameraTarget {
  x: number;
  z: number;
}

export interface ArenaCamera {
  readonly camera: TargetCamera;
  /** Frames the given points (the living fighters) for this frame. */
  update(points: readonly ArenaCameraTarget[], dt: number): void;
  /** Places the camera at its resting framing with no easing, for the first frame of a duel. */
  reset(points: readonly ArenaCameraTarget[]): void;
  /**
   * Keeps the arena out from under whatever is drawn over the canvas: the arena is framed in this
   * part of it and fitted there (`arenaFrame.ts` finds it). The whole canvas is what a headset
   * gets.
   */
  setFrame(frame: ArenaFrame): void;
  /** Where the arena is framed now, as shares of the canvas from its top left. */
  frame(): ArenaFrame;
  dispose(): void;
}

/** Half the diagonal of a framed rectangle: how far apart two fighters can be in it. */
function radiusOf(spanX: number, spanZ: number): number {
  return Math.hypot(spanX, spanZ) / 2;
}

/**
 * The fixed three-quarter camera over a duel (D-16).
 *
 * `fit` frames the whole arena and never moves off its centre; `scroll` frames `camera.region`
 * tiles and tracks the fighters' midpoint across a larger map, clamped so the frame never leaves
 * the arena. The azimuth is deliberately square to the grid rather than turned: the rows an arena
 * was authored in stay rows on screen, which is also what makes screen-relative movement read
 * the way a player expects.
 */
export function createArenaCamera(scene: Scene, arena: ArenaDef, top = 0): ArenaCamera {
  const mode = arena.camera?.mode ?? 'fit';
  const region = arena.camera?.region;
  const spanX = mode === 'scroll' && region ? region[0] : arena.width;
  const spanZ = mode === 'scroll' && region ? region[1] : arena.height;
  const baseDistance = radiusOf(spanX, spanZ) / Math.tan(CAMERA_FOV / 2);
  const centre = { x: arena.width / 2, z: arena.height / 2 };
  /** The framed box's own fit, for the frame's aspect it was last worked out for. */
  let fitted: { aspect: number; fit: Fit } | null = null;
  const fitFor = (aspect: number): Fit => {
    if (fitted && Math.abs(fitted.aspect - aspect) < 1e-6) return fitted.fit;
    const fit = fitArena({
      width: spanX,
      depth: spanZ,
      top: Math.max(LEAST_TOP, top),
      elevation: CAMERA_ELEVATION,
      fov: CAMERA_FOV,
      aspect,
      margin: FIT_MARGIN,
    });
    fitted = { aspect, fit };
    return fit;
  };

  const camera = new TargetCamera('arenaFixed', Vector3.Zero(), scene);
  camera.fov = CAMERA_FOV;
  camera.minZ = 0.5;
  camera.maxZ = baseDistance * 3;

  // Where the frame is centred now, eased towards where it wants to be.
  const at = { x: centre.x, z: centre.z };
  let distance = baseDistance;
  // The part of the canvas the arena is framed in (`setFrame`), as the page measures it.
  let frame: ArenaFrame = WHOLE_CANVAS;
  const perspective = new Matrix();
  const offset = new Matrix();
  const projection = new Matrix();
  const aim = new Vector3();

  /**
   * The frame's own aspect ratio, which is what the fit has to cover; not the canvas's. A canvas
   * collapsed to nothing for a moment reads as square, rather than turning the camera's distance
   * into NaN for the rest of the duel (client-arena:bugs#10).
   */
  const frameAspect = (): number => {
    const engine = scene.getEngine();
    const aspect =
      (engine.getRenderWidth() * frame.width) / (engine.getRenderHeight() * frame.height);
    return Number.isFinite(aspect) && aspect > 0 ? aspect : 1;
  };

  /**
   * The nearest the camera can stand with the whole framed box in the frame: its floor's corners
   * and the tops of its tallest walls, projected at the frame's own aspect (`arenaFit.ts`). Babylon
   * fixes the *vertical* field of view, so a phone held upright sees about 13 degrees across where
   * a desktop window sees the full 28, and stands back further to fit the width.
   *
   * Read every frame rather than once: a phone can be turned, and a window resized.
   */
  const fitDistance = (): number => fitFor(frameAspect()).distance;
  /** How far along the floor from the framed box's middle the camera aims, to sit it evenly. */
  const aimOffset = (): number => fitFor(frameAspect()).aimZ;

  /**
   * Draws the arena into the frame without giving up the rest of the canvas. The whole canvas is
   * rendered through the camera's own perspective, scaled and moved in clip space so that what a
   * camera drawing into the frame alone would show lands exactly there, and the sky fills the rest
   * (a sub-viewport left the canvas's clear colour showing around it). Picking goes through the
   * same matrix, so the cursor still aims where it points. The vertical angle is fixed, so the
   * picture scales by the frame's height both ways; the page's y points down, clip space's up.
   */
  const project = () => {
    if (frame.x === 0 && frame.y === 0 && frame.width === 1 && frame.height === 1) {
      camera.unfreezeProjectionMatrix();
      return;
    }
    const engine = scene.getEngine();
    const reverse = engine.useReverseDepthBuffer;
    Matrix.PerspectiveFovLHToRef(
      camera.fov,
      engine.getAspectRatio(camera),
      reverse ? camera.maxZ : camera.minZ,
      reverse ? camera.minZ : camera.maxZ,
      perspective,
      true,
      engine.isNDCHalfZRange,
      camera.projectionPlaneTilt,
      reverse,
    );
    const scale = frame.height;
    const centreX = 2 * frame.x + frame.width - 1;
    const centreY = 1 - 2 * frame.y - frame.height;
    Matrix.FromValuesToRef(
      scale,
      0,
      0,
      0,
      0,
      scale,
      0,
      0,
      0,
      0,
      1,
      0,
      centreX,
      centreY,
      0,
      1,
      offset,
    );
    perspective.multiplyToRef(offset, projection);
    camera.freezeProjectionMatrix(projection);
  };

  const wantedTarget = (points: readonly ArenaCameraTarget[]): ArenaCameraTarget => {
    // `fit` shows the whole arena, so there is nothing to track: the centre is the framing.
    if (mode !== 'scroll' || points.length === 0) return centre;
    const mid = points.reduce(
      (sum, p) => ({ x: sum.x + p.x / points.length, z: sum.z + p.z / points.length }),
      { x: 0, z: 0 },
    );
    // Keep the framed region inside the arena, so a fight at the edge does not show the void.
    const halfX = Math.min(spanX, arena.width) / 2;
    const halfZ = Math.min(spanZ, arena.height) / 2;
    return {
      x: Math.min(arena.width - halfX, Math.max(halfX, mid.x)),
      z: Math.min(arena.height - halfZ, Math.max(halfZ, mid.z)),
    };
  };

  const wantedDistance = (points: readonly ArenaCameraTarget[]): number => {
    const base = fitDistance();
    if (points.length < 2) return base;
    let apart = 0;
    for (let i = 0; i < points.length; i++)
      for (let j = i + 1; j < points.length; j++)
        apart = Math.max(
          apart,
          Math.hypot(points[i]!.x - points[j]!.x, points[i]!.z - points[j]!.z),
        );
    // 1 when they are on top of each other, 0 while they are a frame's width apart.
    const closeness = 1 - Math.min(1, apart / radiusOf(spanX, spanZ));
    return base * (1 - PUSH_IN * closeness);
  };

  const place = () => {
    // A narrow viewport stands further off than the far plane was first cut for.
    if (distance * 3 > camera.maxZ) camera.maxZ = distance * 3;
    // Behind the first row and above it, looking along +z: tile y is world z, so the arena reads
    // on screen in the order its rows were written.
    const aimZ = at.z + aimOffset();
    camera.position.set(
      at.x,
      Math.sin(CAMERA_ELEVATION) * distance,
      aimZ - Math.cos(CAMERA_ELEVATION) * distance,
    );
    aim.set(at.x, 0, aimZ);
    camera.setTarget(aim);
    project();
  };

  return {
    camera,
    update(points, dt) {
      const target = wantedTarget(points);
      const k = Math.min(1, dt * FOLLOW);
      at.x += (target.x - at.x) * k;
      at.z += (target.z - at.z) * k;
      distance += (wantedDistance(points) - distance) * k;
      place();
    },
    reset(points) {
      const target = wantedTarget(points);
      at.x = target.x;
      at.z = target.z;
      distance = wantedDistance(points);
      place();
    },
    setFrame(next) {
      // A frame that would be too thin either way grows evenly rather than hide the arena.
      const side = (start: number, size: number): [number, number] => {
        const want = Math.max(LEAST_FRAME, clamp01(size));
        const from = clamp01(start) - (want - clamp01(size)) / 2;
        return [Math.max(0, Math.min(1 - want, from)), want];
      };
      const [x, width] = side(next.x, next.width);
      const [y, height] = side(next.y, next.height);
      const same = [frame.x - x, frame.y - y, frame.width - width, frame.height - height].every(
        (d) => Math.abs(d) < 1e-4,
      );
      if (same) return;
      frame = { x, y, width, height };
      // The frame's aspect changes what fits across it; settle there without easing in from afar.
      distance = wantedDistance([]);
      place();
    },
    frame() {
      return frame;
    },
    dispose() {
      camera.dispose();
    },
  };
}
